import { AxiosError } from "axios";
import axios from "@/axios.config";
import { ApiResponse } from "@/types/fetch.type";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Question, QuestionAction, QuestionExportFilters, QuestionActionExportFilters } from "@/types/examination.types";

interface AllQuestionsByCouncilParams {
    enabled?: boolean;
    councilId?: string;
}

export const useGetQuestionsByCouncil = ({ councilId, enabled = true }: AllQuestionsByCouncilParams) => {
    return useQuery<ApiResponse<Question[]>, AxiosError>({
        queryKey: ["questions", councilId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<Question[]>>(`/questions/council/${councilId}`);
            return response.data;
        },
        enabled: enabled && !!councilId,
    });
};

// Get questions by unit with language support
export const useGetQuestionsByUnit = (unitId?: string, language?: string, page = 0, size = 20) => {
    return useQuery<ApiResponse<{ content: Question[]; pageable: any }>, AxiosError>({
        queryKey: ["questions", "unit", unitId, language, page, size],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (language) params.append('language', language);
            params.append('page', page.toString());
            params.append('size', size.toString());
            
            const response = await axios.get<ApiResponse<{ content: Question[]; pageable: any }>>(`/questions/unit/${unitId}?${params}`);
            return response.data;
        },
        enabled: !!unitId,
    });
};

export const useGetQuestionById = (questionId: string, language?: string) => {
    return useQuery<ApiResponse<Question>, AxiosError>({
        queryKey: ["questions", questionId, language],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (language) params.append('language', language);
            
            const url = language ? `/questions/${questionId}?${params}` : `/questions/${questionId}`;
            const response = await axios.get<ApiResponse<Question>>(url);
            return response.data;
        },
    });
};

// Getting question action history
export const useGetQuestionActions = (questionId: string, enabled = true) => {
    return useQuery<ApiResponse<QuestionAction[]>, AxiosError>({
        queryKey: ["question-actions", questionId],
        queryFn: async () => {
            const response = await axios.get<ApiResponse<QuestionAction[]>>(`/questions/${questionId}/actions`);
            return response.data;
        },
        enabled: enabled && !!questionId,
    });
};

export interface CreateQuestionOptionRequest {
    correct: boolean;
    translations: Record<string, string>;
}

export interface CreateQuestionRequest {
    councilId: string;
    options: CreateQuestionOptionRequest[];
    educationProgramId?: string;
    moduleId?: string;
    unitId?: string;
    bloomsLevelIds?: string[];
    unitCode?: string;
    bloomsLevelCodes?: string[];
    media?: File;
    translations: Record<string, string>;
}

// Batch create: accepts an array of CreateQuestionRequest
export const useCreateQuestions = () => {
    const queryClient = useQueryClient();
    
    return useMutation<ApiResponse<Question[]>, AxiosError<{ message: string }>, CreateQuestionRequest[] | CreateQuestionRequest>({
        mutationFn: async (questions) => {
            // Accepts either a single question or an array
            const isArray = Array.isArray(questions);
            const questionsToProcess = isArray ? questions : [questions];
            
            if (isArray && questionsToProcess.length > 1) {
                // For multiple questions, use individual single question endpoint calls
                // since bulk endpoint doesn't support media files
                const results = [];
                for (const question of questionsToProcess) {
                    const formData = new FormData();
                    
                    // Add basic question data (API expects these exact field names)
                    formData.append('councilId', question.councilId);
                    
                    // Only add unitId (not educationProgramId, moduleId, unitCode)
                    if (question.unitId) formData.append('unitId', question.unitId);
                    
                    // Stringify arrays and JSON objects as required by the API
                    if (question.bloomsLevelIds && question.bloomsLevelIds.length > 0) {
                        formData.append('bloomsLevelIds', JSON.stringify(question.bloomsLevelIds));
                    }
                    
                    // Add translations (required, EN must be present) - stringified JSON
                    formData.append('translations', JSON.stringify(question.translations));
                    
                    // Add options as stringified JSON
                    formData.append('options', JSON.stringify(question.options));
                    
                    // Add media file if present
                    if (question.media) {
                        formData.append('media', question.media);
                    }
                    
                    const response = await axios.post<ApiResponse<Question>>("/questions", formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    });
                    results.push(response.data.data);
                }
                
                return {
                    success: true,
                    status: 'SUCCESS',
                    message: `Successfully created ${results.length} questions`,
                    data: results,
                } as ApiResponse<Question[]>;
            } else {
                // Single question
                const question = questionsToProcess[0];
                const formData = new FormData();
                
                // Add basic question data (API expects these exact field names)
                formData.append('councilId', question.councilId);
                
                // Only add unitId (not educationProgramId, moduleId, unitCode)
                if (question.unitId) formData.append('unitId', question.unitId);
                
                // Stringify arrays and JSON objects as required by the API
                if (question.bloomsLevelIds && question.bloomsLevelIds.length > 0) {
                    formData.append('bloomsLevelIds', JSON.stringify(question.bloomsLevelIds));
                }
                
                // Add translations (required, EN must be present) - stringified JSON
                formData.append('translations', JSON.stringify(question.translations));
                
                // Add options as stringified JSON
                formData.append('options', JSON.stringify(question.options));
                
                // Add media file if present
                if (question.media) {
                    formData.append('media', question.media);
                }
                
                const response = await axios.post<ApiResponse<Question>>("/questions", formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
                // Convert single question response to array format for consistency
                return {
                    ...response.data,
                    data: [response.data.data]
                } as ApiResponse<Question[]>;
            }
        },
        onSuccess: (data, variables) => {
            // Get councilId from the first question (all questions should have the same councilId)
            const councilId = Array.isArray(variables) ? variables[0]?.councilId : variables?.councilId;
            
            if (councilId) {
                // Invalidate all question-related queries for this council
                queryClient.invalidateQueries({ queryKey: ['questions', councilId] });
                queryClient.invalidateQueries({ queryKey: ['questions', 'unit'] });
                queryClient.invalidateQueries({ queryKey: ['questions'] });
            }
        },
    });
};

// Update request types allowing options without id for newly created options
export interface UpdateQuestionOptionRequest {
    id?: string;
    correct: boolean;
    translations: Record<string, string> | string;
}

export interface UpdateQuestionRequest {
    id: string;
    councilId?: string;
    educationProgramId?: string;  // 🔄 Changed from categoryId
    moduleId?: string;
    unitId?: string;
    bloomsLevelIds?: string[];
    translations: Record<string, string>;
    options: UpdateQuestionOptionRequest[];
    media?: File;
    // For backward compat where callers may still pass question.council
    council?: { id?: string } | null;
}

export const useUpdateQuestion = () => {
    const queryClient = useQueryClient();
    
    return useMutation<ApiResponse<Question>, AxiosError<{ message: string }>, UpdateQuestionRequest>({
        mutationFn: async (question) => {
            const formData = new FormData();
            
            // Add basic question data
            formData.append('councilId', (question as any).council?.id || question.councilId || '');
            
            if (question.educationProgramId) formData.append('educationProgramId', question.educationProgramId);
            if (question.moduleId) formData.append('moduleId', question.moduleId);
            if (question.unitId) formData.append('unitId', question.unitId);
            
            // Stringify arrays and JSON objects as required by the API
            if (question.bloomsLevelIds) {
                formData.append('bloomsLevelIds', JSON.stringify(question.bloomsLevelIds));
            }
            
            // Add translations (required, EN must be present)
            formData.append('translations', JSON.stringify(question.translations));
            
            // Add options as stringified JSON
            formData.append('options', JSON.stringify(question.options));
            
            // Add media file if present
            if (question.media) {
                formData.append('media', question.media);
            }
            
            const response = await axios.put<ApiResponse<Question>>(`/questions/${question.id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        },
        onSuccess: (data, variables) => {
            const councilId = (variables as any).council?.id || variables.councilId;
            
            if (councilId) {
                // Invalidate all question-related queries for this council
                queryClient.invalidateQueries({ queryKey: ['questions', councilId] });
                queryClient.invalidateQueries({ queryKey: ['questions', 'unit'] });
                queryClient.invalidateQueries({ queryKey: ['questions'] });
                // Also invalidate the specific question query
                queryClient.invalidateQueries({ queryKey: ['questions', variables.id] });
            }
        },
    });
};

export const useDeleteQuestion = () => {
    const queryClient = useQueryClient();
    
    return useMutation<ApiResponse<Question>, AxiosError<{ message: string }>, { questionId: string; councilId?: string }>({
        mutationFn: async ({ questionId }) => {
            const response = await axios.delete<ApiResponse<Question>>(`/questions/${questionId}`);
            return response.data;
        },
        onSuccess: (data, variables) => {
            if (variables.councilId) {
                // Invalidate all question-related queries for this council
                queryClient.invalidateQueries({ queryKey: ['questions', variables.councilId] });
                queryClient.invalidateQueries({ queryKey: ['questions', 'unit'] });
                queryClient.invalidateQueries({ queryKey: ['questions'] });
                // Also invalidate the specific question query
                queryClient.invalidateQueries({ queryKey: ['questions', variables.questionId] });
            }
        },
    });
};

export const useBulkAddQuestions = () => {
    const queryClient = useQueryClient();
    
    return useMutation<ApiResponse<Question[]>, AxiosError<{ message: string }>, CreateQuestionRequest[]>({
        mutationFn: async (questions) => {
            // Submit all questions at once to bulk endpoint (JSON payload)
            // API expects { "questions": [...] } wrapper structure
            const response = await axios.post<ApiResponse<Question[]>>(
                "/questions/bulk",
                { questions },
                { headers: { 'Content-Type': 'application/json' } }
            );
            return response.data;
        },
        onSuccess: (data, variables) => {
            // Get councilId from the first question (all questions should have the same councilId)
            const councilId = variables[0]?.councilId;
            
            if (councilId) {
                // Invalidate all question-related queries for this council
                queryClient.invalidateQueries({ queryKey: ['questions', councilId] });
                queryClient.invalidateQueries({ queryKey: ['questions', 'unit'] });
                queryClient.invalidateQueries({ queryKey: ['questions'] });
            }
        },
    });
};

// Export functionality
export const downloadQuestionsExport = async (format: 'csv' | 'excel', filters?: QuestionExportFilters) => {
    try {
        const params = new URLSearchParams({ format, ...filters });
        const response = await axios.get(`/questions/export?${params}`, {
            responseType: 'blob',
        });
        
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const contentDisposition = response.headers['content-disposition'];
        const fileExtension = format === 'excel' ? 'xlsx' : 'csv';
        let filename = `questions-export.${fileExtension}`;
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }
        
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true, filename };
    } catch (error) {
        console.error('Error downloading questions export:', error);
        throw error;
    }
};

export const downloadQuestionActionsExport = async (format: 'csv' | 'excel', filters?: QuestionActionExportFilters) => {
    try {
        const params = new URLSearchParams({ format, ...filters });
        const response = await axios.get(`/questions/actions/export?${params}`, {
            responseType: 'blob',
        });
        
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const contentDisposition = response.headers['content-disposition'];
        const fileExtension = format === 'excel' ? 'xlsx' : 'csv';
        let filename = `question-actions-export.${fileExtension}`;
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
            if (filenameMatch) {
                filename = filenameMatch[1];
            }
        }
        
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true, filename };
    } catch (error) {
        console.error('Error downloading question actions export:', error);
        throw error;
    }
};

// Media Management Services
export const useUploadQuestionMedia = () => {
    const queryClient = useQueryClient();
    
    return useMutation<ApiResponse<{ questionId: string; mediaPath: string; originalFilename: string; contentType: string; size: number; uploadedAt: string }>, AxiosError<{ message: string }>, { questionId: string; media: File; councilId?: string }>({
        mutationFn: async ({ questionId, media }) => {
            const formData = new FormData();
            formData.append('media', media);
            
            const response = await axios.post<ApiResponse<{ questionId: string; mediaPath: string; originalFilename: string; contentType: string; size: number; uploadedAt: string }>>(
                `/questions/${questionId}/media`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            return response.data;
        },
        onSuccess: (data, variables) => {
            if (variables.councilId) {
                // Invalidate question queries to reflect media changes
                queryClient.invalidateQueries({ queryKey: ['questions', variables.councilId] });
                queryClient.invalidateQueries({ queryKey: ['questions', 'unit'] });
                queryClient.invalidateQueries({ queryKey: ['questions'] });
                queryClient.invalidateQueries({ queryKey: ['questions', variables.questionId] });
            }
        },
    });
};

export const useDeleteQuestionMedia = () => {
    const queryClient = useQueryClient();
    
    return useMutation<ApiResponse<void>, AxiosError<{ message: string }>, { questionId: string; councilId?: string }>({
        mutationFn: async ({ questionId }) => {
            const response = await axios.delete<ApiResponse<void>>(`/questions/${questionId}/media`);
            return response.data;
        },
        onSuccess: (data, variables) => {
            if (variables.councilId) {
                // Invalidate question queries to reflect media changes
                queryClient.invalidateQueries({ queryKey: ['questions', variables.councilId] });
                queryClient.invalidateQueries({ queryKey: ['questions', 'unit'] });
                queryClient.invalidateQueries({ queryKey: ['questions'] });
                queryClient.invalidateQueries({ queryKey: ['questions', variables.questionId] });
            }
        },
    });
};

export const getQuestionMediaUrl = (questionId: string) => {
    return `/questions/${questionId}/media`;
};

export const downloadQuestionMedia = async (questionId: string, filename?: string) => {
    try {
        const response = await axios.get(`/questions/${questionId}/media`, {
            responseType: 'blob',
        });
        
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const contentDisposition = response.headers['content-disposition'];
        let downloadFilename = filename || `question-media-${questionId}`;
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
            if (filenameMatch) {
                downloadFilename = filenameMatch[1];
            }
        }
        
        link.download = downloadFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true, filename: downloadFilename };
    } catch (error) {
        console.error('Error downloading question media:', error);
        throw error;
    }
};
